import { FunctionComponent, useCallback, useEffect, useState, useMemo, useRef } from "react"
import React, { Suspense } from "react"
import { DndContext, rectIntersection } from "@dnd-kit/core"
import { restrictToWindowEdges } from "@dnd-kit/modifiers"
import moment from "moment"
import { useNavigate, useParams } from "react-router-dom"
import {
  useGetUserapiAdminTestsGetTestByIdByTestIdQuery,
  useGetUserapiResultsGetSavepointByRunIdByRunIdQuery,
  usePostUserapiResultsSavepointByRunIdMutation,
  usePutUserapiResultsFinalResultsByRunIdMutation,
} from "../app/services/meseeknaApi"
import { useGetTeamSimulationDetailsQuery, useAutoStartTeamSimulationMutation } from "../app/services/meseeknaApiV2"
import { useCreateRoomMutation, useFindOrCreateRoomMutation, useJoinRoomMutation, useGetRoomParticipantsQuery, useGetRoomChoicesQuery, isCorsOrNetworkError, getApiErrorMessage } from "../app/services/collaborationApi"
import { FetchBaseQueryError } from "@reduxjs/toolkit/query/react"
import { CreateRoomRequest } from "../features/collaboration/types"
import { RootState, useTypedDispatch, useTypedSelector } from "../app/store"
import HeaderSection from "../components/header-section"
import MessagesWithSearch from "../components/messages-with-search"
import SidebarMinimal from "../features/sidebar/components/sidebar-minimal"
import SimulationHeader from "../components/simulation-header"

import ModalBreaktime from "../features/simulation/components/modal-breaktime"
import {
  setSecondsElapsed,
  simulationSlice,
  addMessage,
  setupSimulation,
  destroyTimer,
} from "../features/simulation/simulationSlice"
import {
  createTimer,
  resetSimulationSafe,
  resumeTimer,
} from "../features/simulation/simulationThunks"
import {
  SavePointData,
  SavePointLogType,
  SavePointRes,
} from "../features/simulation/types"
import { SAVEPOINT_TIMER } from "../utils/constants"
import { convertToSeconds } from "../features/simulation/utils"
import NotificationContainer from "../features/auth/components/Notifications/NotificationContainer"

import { MessagesWithExtraType } from "../features/simulation-messages/types"
import { mergeCollaborativeData, createCollaborativeFinalResults } from "../features/collaboration/utils/dataIntegration"

// Enhanced collaboration components - with error handling
import {
  setCurrentRoom,
  setCurrentUserId,
  setParticipants,
  addParticipant,
  setPendingChoices,
  resetCollaboration,
} from "../features/collaboration/collaborationSlice"
import { SimulationRoom, RoomStatus, ParticipantRole, ParticipantStatus, VotingThreshold, CollaborationState } from "../features/collaboration/types"
import { useCollaboration } from "../features/collaboration/hooks/useCollaboration"
import { UserGroupIcon } from "@heroicons/react/24/outline"

// Default collaboration state - moved outside component to prevent recreation on each render
const DEFAULT_COLLABORATION_STATE: CollaborationState = {
  votingModalOpen: false,
  currentRoom: null,
  participants: [],
  currentUserId: null,
  chatMessages: [],
  privateConversations: [],
  activeConversationId: null,
  isConnected: false,
  pendingChoices: [],
  completedChoices: [],
  currentVotingChoice: null,
  transcription: {
    segments: [],
    partialSegments: [],
    isTranscriptionEnabled: false,
    isTranscriptionActive: false,
    transcriptionErrors: []
  },
  sessionTranscripts: [],
  currentSessionTranscript: null
}

// Import the new team collaboration components
import TeamActionsCenter from "../features/collaboration/components/TeamActionsCenter"
import LiveKitVideoGrid from "../features/collaboration/components/LiveKitVideoGrid"
import VotingPanel from "../features/collaboration/components/VotingPanel"

// Lazy load fallback components to prevent import errors
const VotingModal = React.lazy(() => 
  import("../features/collaboration/components/VotingModal").catch(() => ({
    default: () => null
  }))
)

const CreateRoomModal = React.lazy(() =>
  import("../features/collaboration/components/CreateRoomModal").catch(() => ({
    default: () => null
  }))
)

// BUG FIX: Error boundary to catch and recover from crashes in simulation components
// This prevents a white-out when errors occur in voting, execution, or other areas
class SimulationErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('❌ [SimulationErrorBoundary] Caught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full bg-red-50 rounded-lg p-8">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-red-700 mb-2">Something went wrong</h3>
            <p className="text-sm text-red-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const CollaborativeSimulation: FunctionComponent = () => {
  // Removed excessive render logging that was causing performance issues

  const dispatch = useTypedDispatch()
  const navigate = useNavigate()
  const { testId: testIdParam, runId: runIdParam, teamSimulationId } = useParams()

  // Initialize collaboration hook to set up LiveKit event listeners for real-time updates
  // This is critical for vote syncing, choice proposals, and participant updates via LiveKit
  useCollaboration()

  // Consolidated loading state
  const [isAppReady, setIsAppReady] = useState(false)
  const [initializationStep, setInitializationStep] = useState('starting')

  // Flag to prevent multiple final save calls (prevents duplicate downloads on join)
  const [hasSavedFinalData, setHasSavedFinalData] = useState(false)

  // Check if this is a demo mode (only for actual demo routes, not real scenarios)
  const isDemoMode = useMemo(() => {
    return window.location.pathname.includes('/demo/')
    // Removed '/roots-and-shoots' check - this should load real scenario data
  }, [])

  // Check if this is a team simulation (different URL structure)
  const isTeamSimulation = useMemo(() => {
    const result = !!teamSimulationId && window.location.pathname.includes('/team-simulations/')
    console.log('isTeamSimulation check:', { teamSimulationId, pathname: window.location.pathname, result })
    return result
  }, [teamSimulationId])

  // Get team simulation details to extract the dynamic testId
  const {
    data: teamSimulationData,
    error: teamSimulationError,
    isLoading: isLoadingTeamSimulation,
    refetch: refetchTeamSimulation
  } = useGetTeamSimulationDetailsQuery(
    { teamSimulationId: teamSimulationId! },
    {
      skip: !isTeamSimulation || !teamSimulationId,
      refetchOnMountOrArgChange: true,
      // Poll every 3 seconds to catch status changes from other sources (admin start, etc.)
      pollingInterval: 3000
    }
  )

  // Log team simulation API status for debugging
  useEffect(() => {
    if (isTeamSimulation) {
      console.log('Team simulation API status:', {
        teamSimulationId,
        isLoading: isLoadingTeamSimulation,
        hasData: !!teamSimulationData,
        error: teamSimulationError
      })
    }
  }, [isTeamSimulation, teamSimulationId, isLoadingTeamSimulation, teamSimulationData, teamSimulationError])

  // Track if collaboration state has been reset (to prevent race condition with auto-join)
  const [hasResetCollaboration, setHasResetCollaboration] = useState(false)

  // Track if simulation has started (past scheduled start time)
  const [simulationStarted, setSimulationStarted] = useState(false)
  const [timeUntilStart, setTimeUntilStart] = useState<number | null>(null)
  // Track the confirmed status to prevent flickering during status transitions
  const [confirmedStatus, setConfirmedStatus] = useState<string | null>(null)
  const statusTransitionRef = useRef<NodeJS.Timeout | null>(null)
  // Track if auto-start is in progress to show "starting..." state instead of waiting room
  const [isAutoStarting, setIsAutoStarting] = useState(false)

  // Auto-start mutation for scheduled team simulations
  // Moved here so it's available before the useEffect that uses it
  const [autoStartTeamSimulation] = useAutoStartTeamSimulationMutation()
  // Track if auto-start has been attempted to prevent duplicate calls
  const [autoStartAttempted, setAutoStartAttempted] = useState(false)

  // Reset collaboration AND simulation state on mount for team simulations to clear any stale persisted data
  useEffect(() => {
    if (isTeamSimulation) {
      console.log('🔄 Resetting collaboration and simulation state for team simulation')
      dispatch(resetCollaboration())
      // Also reset simulation state to clear old messages, timer, etc.
      dispatch(resetSimulationSafe())
      // Reset auto-start flag for this simulation
      setAutoStartAttempted(false)
      // Mark reset as complete after a small delay to ensure Redux state is updated
      setTimeout(() => {
        setHasResetCollaboration(true)
        console.log('✅ Collaboration and simulation state reset complete')
      }, 100)
    } else {
      // For non-team simulations, no reset needed
      setHasResetCollaboration(true)
    }
  }, [teamSimulationId, isTeamSimulation, dispatch])

  // Check if simulation is past scheduled start time (lobby logic)
  // Uses a stabilization mechanism to prevent flickering during status transitions
  useEffect(() => {
    // Non-team simulations always start immediately
    if (!isTeamSimulation) {
      setSimulationStarted(true)
      setConfirmedStatus('active')
      return
    }

    // For team simulations, wait for data to load before deciding
    // Don't set simulationStarted=true prematurely while data is loading
    if (!teamSimulationData?.data) {
      console.log('⏳ Waiting for team simulation data to load before checking start time...')
      // BUG FIX: Explicitly keep in waiting room while loading
      setSimulationStarted(false)
      // Don't update confirmedStatus - keep it null until we have data
      return // Stay in waiting room until data loads
    }

    const simData = teamSimulationData.data
    const scheduledTime = simData.scheduledStartTime
    const currentStatus = simData.status

    // Log state for debugging (removed popup alert)
    console.log('🔍 Team simulation state check:', {
      currentStatus,
      scheduledTime: scheduledTime ? new Date(scheduledTime).toLocaleString() : 'none',
      now: new Date().toLocaleString(),
      confirmedStatus,
      simulationStarted,
      isAutoStarting,
      autoStartAttempted,
    })

    // BUG FIX: Debounce status transitions to prevent flickering
    // When status changes, wait a short period to confirm it's stable
    const updateConfirmedStatus = (newStatus: string, startSimulation: boolean) => {
      // Clear any pending transition
      if (statusTransitionRef.current) {
        clearTimeout(statusTransitionRef.current)
      }

      // If transitioning TO active, add a small delay to ensure state is stable
      if (newStatus === 'active' && confirmedStatus !== 'active') {
        console.log('📅 Status transitioning to active - stabilizing...')
        statusTransitionRef.current = setTimeout(() => {
          console.log('📅 Status confirmed as active - starting simulation')
          setConfirmedStatus('active')
          setSimulationStarted(true)
          setTimeUntilStart(null)
          // Clear auto-starting flag now that we're definitely active
          setIsAutoStarting(false)
        }, 100) // Small delay to let other state updates settle
      } else if (newStatus === 'pending') {
        // Immediately set to pending (waiting room is safe to show)
        setConfirmedStatus('pending')
        setSimulationStarted(false)
      } else {
        // Other statuses
        setConfirmedStatus(newStatus)
        setSimulationStarted(startSimulation)
        // Clear auto-starting flag for any other status transition
        setIsAutoStarting(false)
      }
    }

    // If simulation status is already 'active', it's started (admin has started it)
    if (currentStatus === 'active') {
      console.log('📅 Simulation is active - starting')
      updateConfirmedStatus('active', true)
      return
    }

    // If status is 'pending', show waiting room
    // The simulation will start when either:
    // 1. The scheduled time arrives (if set)
    // 2. The admin starts it (status becomes 'active')
    if (currentStatus === 'pending') {
      console.log('📅 Simulation is pending - showing waiting room')

      // BUG FIX: Reset isAutoStarting when status is pending
      // This ensures we don't incorrectly hide the waiting room
      setIsAutoStarting(false)

      // BUG FIX: Immediately set to pending to show waiting room
      updateConfirmedStatus('pending', false)

      // If there's a scheduled time, set up countdown
      if (scheduledTime) {
        let intervalRef: NodeJS.Timeout | null = null
        let isCancelled = false

        const triggerAutoStart = async () => {
          // Time to start! But we need to call the backend to actually start it
          // Don't transition locally - let the API call update the status
          // which will be picked up by polling or refetch
          if (!autoStartAttempted && teamSimulationId && !isCancelled) {
            console.log('🎉 Scheduled start time reached - calling backend auto-start...')
            setAutoStartAttempted(true)
            // BUG FIX: Set isAutoStarting to prevent showing waiting room briefly
            setIsAutoStarting(true)
            try {
              const result = await autoStartTeamSimulation({ teamSimulationId }).unwrap()
              console.log('📡 Auto-start API response:', result)
              if (result.status === 'success') {
                // Refetch to get updated status
                console.log('✅ Auto-start successful, refetching simulation data...')
                refetchTeamSimulation()
              } else {
                // The simulation might have already been started by someone else
                // or conditions weren't met - refetch anyway to get current status
                console.log('ℹ️ Auto-start returned:', result.message)
                refetchTeamSimulation()
              }
            } catch (error) {
              console.error('❌ Auto-start failed:', error)
              // Refetch to get current status in case another participant started it
              refetchTeamSimulation()
            } finally {
              // Only clear isAutoStarting once the status has been refetched
              // The status change will naturally transition us to the simulation view
              // Don't set isAutoStarting = false here - let the status update handle it
            }
          }
        }

        const checkStartTime = () => {
          if (isCancelled) return true // Stop if cancelled

          const now = new Date().getTime()
          const startTime = new Date(scheduledTime).getTime()
          const diff = startTime - now

          if (diff <= 0) {
            // Time to start - trigger auto-start asynchronously
            // BUG FIX: Set isAutoStarting immediately to prevent waiting room flicker
            setIsAutoStarting(true)
            triggerAutoStart()
            return true // Stop the interval
          } else {
            // Keep in waiting room while countdown is active
            setTimeUntilStart(Math.ceil(diff / 1000)) // Time in seconds
            return false
          }
        }

        // Check immediately - if time has passed, set isAutoStarting BEFORE any render
        const now = new Date().getTime()
        const startTime = new Date(scheduledTime).getTime()
        if (startTime - now <= 0 && !autoStartAttempted) {
          // BUG FIX: Pre-emptively set isAutoStarting to prevent waiting room flicker
          setIsAutoStarting(true)
        }

        const shouldStop = checkStartTime()

        // Only set up interval if we haven't reached start time yet
        if (!shouldStop) {
          intervalRef = setInterval(() => {
            if (checkStartTime()) {
              if (intervalRef) {
                clearInterval(intervalRef)
                intervalRef = null
              }
            }
          }, 1000)
        }

        return () => {
          isCancelled = true
          if (intervalRef) {
            clearInterval(intervalRef)
          }
          if (statusTransitionRef.current) {
            clearTimeout(statusTransitionRef.current)
          }
        }
      } else {
        // No scheduled time - just show waiting room without countdown
        // Simulation will start when admin changes status to 'active'
        setTimeUntilStart(null)
      }
      return
    }

    // For other statuses (completed, cancelled), don't show simulation
    console.log('📅 Simulation status:', currentStatus)
    updateConfirmedStatus(currentStatus, false)

    return () => {
      if (statusTransitionRef.current) {
        clearTimeout(statusTransitionRef.current)
      }
    }
  // NOTE: confirmedStatus is intentionally NOT in the dependency array
  // We set it inside this effect but don't want the effect to re-run when it changes
  }, [isTeamSimulation, teamSimulationData, autoStartAttempted, teamSimulationId, autoStartTeamSimulation, refetchTeamSimulation]) // eslint-disable-line

  // Ensure we have stable parameters based on the route type
  const stableTestId = useMemo(() => {
    console.log('Debugging testId calculation:', { 
      isDemoMode, 
      isTeamSimulation, 
      testIdParam, 
      teamSimulationId,
      teamSimulationData,
      pathname: window.location.pathname 
    })
    
    if (isDemoMode) return 1
    if (isTeamSimulation && teamSimulationData?.data?.scenarioId) {
      // Use dynamic scenarioId from team simulation data
      console.log('Using dynamic testId from team simulation:', teamSimulationData.data.scenarioId)
      return teamSimulationData.data.scenarioId
    }
    if (isTeamSimulation) {
      // Fallback for team simulations - if API fails or data not loaded, use default
      console.log('Using fallback testId for team simulation (API not available or failed)')
      return 8
    }
    return parseInt(testIdParam as string) || 8 // Default to 8 if no testId
  }, [isDemoMode, isTeamSimulation, testIdParam, teamSimulationId, teamSimulationData])
  
  const stableRunId = useMemo(() => {
    if (isDemoMode) return '1'
    if (isTeamSimulation) return '1' // Team simulations don't use runId in the same way
    return runIdParam || '1' // Default to '1' if no runId
  }, [isDemoMode, isTeamSimulation, runIdParam])
  
  const [runId] = useState(stableRunId)
  
  // Collaboration state
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false)
  const [isAutoJoining, setIsAutoJoining] = useState(false)
  const [autoJoinError, setAutoJoinError] = useState<string | null>(null)
  const [autoJoinRetryCount, setAutoJoinRetryCount] = useState(0)
  const MAX_AUTO_JOIN_RETRIES = 5
  const [createRoom] = useCreateRoomMutation()
  const [findOrCreateRoom] = useFindOrCreateRoomMutation()
  const [joinRoom] = useJoinRoomMutation()

  // Handler for room creation - creates room and auto-joins the creator
  const handleCreateRoom = useCallback(async (roomData: CreateRoomRequest) => {
    try {
      console.log('Creating room with data:', roomData)
      const result = await createRoom(roomData).unwrap()
      console.log('Room created successfully:', result)

      // After room is created, auto-join as the creator
      if (result?.data) {
        const roomId = result.data.id
        const userId = result.data.createdBy // The user who created the room

        console.log('Auto-joining room:', { roomId, userId })
        const joinResult = await joinRoom({ roomId, userId }).unwrap()
        console.log('Joined room successfully:', joinResult)

        if (joinResult?.data) {
          // Set room and add participant from join response
          // BUG FIX: Use addParticipant instead of setParticipants to not overwrite other participants
          dispatch(setCurrentRoom(joinResult.data.room))
          dispatch(addParticipant(joinResult.data.participant))
          dispatch(setCurrentUserId(userId))
        }
      }
    } catch (error) {
      console.error('Failed to create or join room:', error)
    }
  }, [createRoom, joinRoom, addParticipant, dispatch])

  // Get current user info for auto-join
  const { user } = useTypedSelector((state: RootState) => state.authSlice)

  // Get collaboration state for auto-join check
  const collaborationSliceForAutoJoin = useTypedSelector((state: RootState) => state.collaborationSlice)
  const hasCurrentRoom = !!collaborationSliceForAutoJoin?.currentRoom

  // Auto-join room for team simulations - creates room if needed and joins automatically
  useEffect(() => {
    // Debug: Log all conditions for auto-join
    console.log('🔍 Auto-join check:', {
      isTeamSimulation,
      hasTeamSimulationData: !!teamSimulationData?.data,
      hasCurrentRoom,
      isAutoJoining,
      isAppReady,
      hasResetCollaboration,
      autoJoinRetryCount,
      maxRetries: MAX_AUTO_JOIN_RETRIES,
      shouldAutoJoin: isTeamSimulation && teamSimulationData?.data && !hasCurrentRoom && !isAutoJoining && isAppReady && hasResetCollaboration && autoJoinRetryCount < MAX_AUTO_JOIN_RETRIES
    })

    // Only auto-join for team simulations when:
    // 1. isTeamSimulation is true
    // 2. teamSimulationData is loaded
    // 3. No currentRoom exists (checked AFTER reset is complete)
    // 4. Not already auto-joining
    // 5. App is ready
    // 6. Collaboration state has been reset (prevents race condition)
    // 7. Haven't exceeded max retry attempts
    if (!isTeamSimulation || !teamSimulationData?.data || hasCurrentRoom || isAutoJoining || !isAppReady || !hasResetCollaboration) {
      return
    }

    // Stop retrying after max attempts to prevent endless loop
    if (autoJoinRetryCount >= MAX_AUTO_JOIN_RETRIES) {
      console.error(`❌ Auto-join failed after ${MAX_AUTO_JOIN_RETRIES} attempts. Please refresh to try again.`)
      return
    }

    const autoJoinTeamSimulation = async () => {
      // Exponential backoff: wait before retrying (0, 1s, 2s, 4s, 8s)
      if (autoJoinRetryCount > 0) {
        const backoffMs = Math.min(1000 * Math.pow(2, autoJoinRetryCount - 1), 8000)
        console.log(`⏳ Waiting ${backoffMs}ms before retry attempt ${autoJoinRetryCount + 1}/${MAX_AUTO_JOIN_RETRIES}...`)
        await new Promise(resolve => setTimeout(resolve, backoffMs))
      }

      setIsAutoJoining(true)
      setAutoJoinError(null)

      try {
        const simData = teamSimulationData.data
        const userId = user?.id || parseInt(sessionStorage.getItem('teamSimUserId') || sessionStorage.getItem('userId') || '0')

        // Get user name from multiple sources (Redux user state, localStorage)
        let userName = 'Participant'
        if (user?.firstName && user?.lastName) {
          userName = `${user.firstName} ${user.lastName}`
        } else {
          // Try sessionStorage - set by team-simulation-login.tsx (per-tab isolation)
          const storedUserName = sessionStorage.getItem('userName')
          const storedFirstName = sessionStorage.getItem('firstName')
          const storedLastName = sessionStorage.getItem('lastName')

          if (storedUserName) {
            userName = storedUserName
          } else if (storedFirstName || storedLastName) {
            userName = `${storedFirstName || ''} ${storedLastName || ''}`.trim() || 'Participant'
          }
        }

        if (!userId) {
          throw new Error('No user ID found. Please log in again.')
        }

        console.log('🚀 Auto-joining team simulation:', {
          teamSimulationId,
          simName: simData.name,
          scenarioId: simData.scenarioId,
          userId,
          userName
        })

        // First, try to find an existing room for this team simulation
        // If not found, create one using findOrCreateRoom
        // maxParticipants should be at least 10, or more if there are more invited participants
        const participantCount = simData.participants?.length || 0
        const maxParticipants = Math.max(participantCount, 10)

        const roomData: CreateRoomRequest = {
          testId: simData.scenarioId,
          organizationId: simData.organizationId,
          teamSimulationId: teamSimulationId, // Key for finding the same room for all participants
          name: simData.name || `Team Simulation ${teamSimulationId}`,
          description: `Team simulation session for ${simData.name}`,
          maxParticipants,
          votingThreshold: VotingThreshold.SIMPLE_MAJORITY,
          votingTimeLimit: 300,
        }

        console.log('📝 Finding or creating room:', roomData)

        // Use findOrCreateRoom to ensure all participants join the same room
        const createResult = await findOrCreateRoom(roomData).unwrap()
        console.log('✅ Room found/created:', createResult)

        if (createResult?.data) {
          const roomId = createResult.data.id

          // Parse user name into first/last for the join request
          const nameParts = userName.split(' ')
          const firstName = nameParts[0] || 'Unknown'
          const lastName = nameParts.slice(1).join(' ') || 'User'

          // Now join the room with participant name
          console.log('🚪 Joining room:', { roomId, userId, firstName, lastName })
          const joinResult = await joinRoom({ roomId, userId, firstName, lastName }).unwrap()
          console.log('✅ Joined room:', joinResult)

          if (joinResult?.data) {
            // Set room and add participant in Redux state
            // BUG FIX: Use addParticipant instead of setParticipants to not overwrite other participants
            dispatch(setCurrentRoom(joinResult.data.room))
            dispatch(addParticipant(joinResult.data.participant))
            dispatch(setCurrentUserId(userId))
            console.log('🎉 Auto-join complete! Room:', joinResult.data.room.name)

            // Restore timer from room's startedAt timestamp to prevent reset on refresh
            const room = joinResult.data.room
            if (room?.startedAt) {
              const startedAtTime = new Date(room.startedAt).getTime()
              const now = Date.now()
              const elapsedSeconds = Math.floor((now - startedAtTime) / 1000)

              if (elapsedSeconds > 0) {
                console.log('⏱️ Restoring timer from room startedAt:', elapsedSeconds, 'seconds')
                dispatch(setSecondsElapsed(elapsedSeconds))
              }
            }
          }
        }
      } catch (error) {
        console.error(`❌ Auto-join failed (attempt ${autoJoinRetryCount + 1}/${MAX_AUTO_JOIN_RETRIES}):`, error)
        const errorMessage = error instanceof Error ? error.message : 'Failed to join team simulation'
        setAutoJoinRetryCount(prev => prev + 1)
        if (autoJoinRetryCount + 1 >= MAX_AUTO_JOIN_RETRIES) {
          setAutoJoinError(`${errorMessage}. Max retries reached - please refresh the page.`)
        } else {
          setAutoJoinError(`${errorMessage}. Retrying...`)
        }
      } finally {
        setIsAutoJoining(false)
      }
    }

    autoJoinTeamSimulation()
  }, [isTeamSimulation, teamSimulationData, hasCurrentRoom, isAutoJoining, isAppReady, hasResetCollaboration, teamSimulationId, user, findOrCreateRoom, joinRoom, dispatch, autoJoinRetryCount])

  // Poll for participants when in a room - this ensures all users see each other
  const roomIdForParticipants = collaborationSliceForAutoJoin?.currentRoom?.id
  const [participantsErrorCount, setParticipantsErrorCount] = useState(0)
  const MAX_PARTICIPANTS_ERRORS = 3 // Stop polling after 3 consecutive errors

  // Only poll if we haven't hit too many errors
  const shouldPollParticipants = roomIdForParticipants && participantsErrorCount < MAX_PARTICIPANTS_ERRORS

  const {
    data: participantsData,
    error: participantsError,
    isError: isParticipantsError,
  } = useGetRoomParticipantsQuery(
    roomIdForParticipants || '',
    {
      skip: !shouldPollParticipants,
      // Stop polling on errors to prevent flooding console with CORS errors
      pollingInterval: shouldPollParticipants ? 5000 : 0,
    }
  )

  // Track errors and stop polling after repeated failures
  useEffect(() => {
    if (isParticipantsError && participantsError) {
      const isCorsError = isCorsOrNetworkError(participantsError as FetchBaseQueryError)
      const errorMessage = getApiErrorMessage(participantsError as FetchBaseQueryError)

      // Only increment error count for network/CORS errors (not 401/403 which might be transient)
      if (isCorsError) {
        setParticipantsErrorCount(prev => {
          const newCount = prev + 1
          if (newCount === 1) {
            console.warn('⚠️ Participants API error - will retry:', errorMessage)
          } else if (newCount >= MAX_PARTICIPANTS_ERRORS) {
            console.error('❌ Participants API failing repeatedly - stopping polling. Error:', errorMessage)
          }
          return newCount
        })
      }
    }
  }, [isParticipantsError, participantsError])

  // Reset error count when room changes or we get successful data
  useEffect(() => {
    if (participantsData?.data) {
      setParticipantsErrorCount(0)
    }
  }, [participantsData])

  // Reset error count when room changes
  useEffect(() => {
    setParticipantsErrorCount(0)
  }, [roomIdForParticipants])

  // Update participants in Redux when they change
  useEffect(() => {
    if (participantsData?.data && participantsData.data.length > 0) {
      console.log('📥 Updating participants from server:', participantsData.data.length, 'participants')
      dispatch(setParticipants(participantsData.data))
    }
  }, [participantsData, dispatch])

  // Poll for choices when in a room - this ensures votes sync across participants
  // This is a fallback mechanism in case LiveKit data channels aren't working
  const [choicesErrorCount, setChoicesErrorCount] = useState(0)
  const MAX_CHOICES_ERRORS = 3 // Stop polling after 3 consecutive errors

  // Only poll if we haven't hit too many errors
  const shouldPollChoices = roomIdForParticipants && choicesErrorCount < MAX_CHOICES_ERRORS

  const {
    data: choicesData,
    error: choicesError,
    isError: isChoicesError,
  } = useGetRoomChoicesQuery(
    { roomId: roomIdForParticipants || '', status: 'pending' },
    {
      skip: !shouldPollChoices,
      // Poll every 3 seconds to keep choices/votes in sync
      pollingInterval: shouldPollChoices ? 3000 : 0,
    }
  )

  // Track errors and stop polling after repeated failures
  useEffect(() => {
    if (isChoicesError && choicesError) {
      const isCorsError = isCorsOrNetworkError(choicesError as FetchBaseQueryError)
      const errorMessage = getApiErrorMessage(choicesError as FetchBaseQueryError)

      // Only increment error count for network/CORS errors
      if (isCorsError) {
        setChoicesErrorCount(prev => {
          const newCount = prev + 1
          if (newCount === 1) {
            console.warn('⚠️ Choices API error - will retry:', errorMessage)
          } else if (newCount >= MAX_CHOICES_ERRORS) {
            console.error('❌ Choices API failing repeatedly - stopping polling. Error:', errorMessage)
          }
          return newCount
        })
      }
    }
  }, [isChoicesError, choicesError])

  // Reset error count when room changes or we get successful data
  useEffect(() => {
    if (choicesData?.data) {
      setChoicesErrorCount(0)
    }
  }, [choicesData])

  // Reset error count when room changes
  useEffect(() => {
    setChoicesErrorCount(0)
  }, [roomIdForParticipants])

  // Update pending choices in Redux when they change from the server
  // This syncs votes across participants even if LiveKit data channels fail
  useEffect(() => {
    if (choicesData?.data) {
      console.log('📥 Updating pending choices from server:', choicesData.data.length, 'choices')
      dispatch(setPendingChoices(choicesData.data))
    }
  }, [choicesData, dispatch])

  const [isModalBreaktimeOpen, setModalBreaktimeOpen] = useState(false)

  const { duration, timer, secondsElapsed, messages, isMessagesLoaded } =
    useTypedSelector((state: RootState) => {
      return state.simulationSlice
    })
  const { organization } = useTypedSelector((state: RootState) => {
    return state.authSlice
  })

  // Safely access collaboration state - returns stable reference when state exists
  const collaborationSlice = useTypedSelector((state: RootState) => state.collaborationSlice)

  // Memoize the collaboration state to prevent infinite re-renders
  const collaborationState = useMemo(() => {
    if (!collaborationSlice) {
      return DEFAULT_COLLABORATION_STATE
    }
    return {
      votingModalOpen: collaborationSlice.votingModalOpen || false,
      currentRoom: collaborationSlice.currentRoom,
      participants: collaborationSlice.participants || [],
      currentUserId: collaborationSlice.currentUserId,
      chatMessages: collaborationSlice.chatMessages || [],
      privateConversations: collaborationSlice.privateConversations || [],
      activeConversationId: collaborationSlice.activeConversationId || null,
      isConnected: collaborationSlice.isConnected || false,
      pendingChoices: collaborationSlice.pendingChoices || [],
      completedChoices: collaborationSlice.completedChoices || [],
      currentVotingChoice: collaborationSlice.currentVotingChoice || null,
      transcription: collaborationSlice.transcription || DEFAULT_COLLABORATION_STATE.transcription,
      sessionTranscripts: collaborationSlice.sessionTranscripts || [],
      currentSessionTranscript: collaborationSlice.currentSessionTranscript || null
    }
  }, [collaborationSlice])

  const { votingModalOpen, currentRoom, participants, currentUserId } = collaborationState

  // Memoize the collaboration session to prevent infinite re-renders
  const collaborationSession = useMemo(() => {
    if (!currentRoom || !currentUserId) return null

    const currentParticipant = participants.find(p => p.userId === currentUserId)

    // Get participant name from multiple sources
    let participantName = `User ${currentUserId}`
    if (currentParticipant?.firstName || currentParticipant?.lastName) {
      participantName = `${currentParticipant.firstName || ''} ${currentParticipant.lastName || ''}`.trim()
    } else {
      // Try sessionStorage as fallback (set by team-simulation-login.tsx, per-tab isolation)
      const storedUserName = sessionStorage.getItem('userName')
      const storedFirstName = sessionStorage.getItem('firstName')
      const storedLastName = sessionStorage.getItem('lastName')

      if (storedUserName) {
        participantName = storedUserName
      } else if (storedFirstName || storedLastName) {
        participantName = `${storedFirstName || ''} ${storedLastName || ''}`.trim()
      }
    }

    return {
      userId: currentUserId.toString(),
      roomName: currentRoom.id,
      participantName,
      simulationId: stableTestId.toString(),
      organizationId: (isDemoMode ? 1 : (organization?.id ? parseInt(organization.id) : 1)).toString()
    }
  }, [currentRoom, currentUserId, participants, stableTestId, isDemoMode, organization?.id])

  // API hooks with skip for demo mode and team simulations
  // Team simulations use a hardcoded runId of '1' which causes 404 errors
  const { data: getSavePointAPIData } =
    useGetUserapiResultsGetSavepointByRunIdByRunIdQuery({
      runId: Number(runId),
    }, {
      skip: isDemoMode || isTeamSimulation
    })

  const [postSavepointByRunId] = usePostUserapiResultsSavepointByRunIdMutation()
  const [postFinalResults] = usePutUserapiResultsFinalResultsByRunIdMutation()

  // Initialize final data state for collaborative simulation
  const initialFinalData: SavePointData = {
    id: 0,
    startDate: "",
    endDate: "",
    length: "",
    time: "",
    name: "",
    log: [],
  }
  const [finalData, setFinalData] = useState(initialFinalData)

  const testId = stableTestId

  const {
    data: simResponse,
    isSuccess: simLoadIsSuccess,
    isFetching: simLoadIsFetching,
  } = useGetUserapiAdminTestsGetTestByIdByTestIdQuery({
    testId: testId,
  }, {
    skip: isDemoMode // Team simulations will load real data
  })

  // Callbacks
  const closeModalBreaktime = useCallback(() => {
    dispatch(resumeTimer())
    setModalBreaktimeOpen(false)
  }, [dispatch])
  
  

  const callSavePoint = useCallback(async () => {
    if (isDemoMode || isTeamSimulation) return // Skip for demo mode and team simulations

    if (
      getSavePointAPIData &&
      "code" in getSavePointAPIData &&
      getSavePointAPIData?.code === 200 &&
      "data" in getSavePointAPIData &&
      Array.isArray(getSavePointAPIData.data) &&
      getSavePointAPIData.data.length > 0
    ) {
      const data = getSavePointAPIData.data as SavePointRes[]
      let resData
      if (data && data.length > 0) {
        if (typeof data[0].data === "string") {
          resData = JSON.parse(data[0].data)
        } else {
          resData = data[0].data
        }
      }

      const { elapsedTime } = data[0]
      let time = 0
      if (elapsedTime !== null) {
        time = moment(elapsedTime, "HH:mm:ss").diff(
          moment().startOf("day"),
          "seconds",
        )
      }
      dispatch(setSecondsElapsed(time))
      let logData: SavePointLogType[] = []
      if (typeof resData === "object" && resData !== null && "log" in resData) {
        logData = resData.log ?? []
        dispatch(simulationSlice.actions.setMessages(logData))
        dispatch(simulationSlice.actions.setPlans(logData))
      }
    }
  }, [getSavePointAPIData, dispatch, isDemoMode])

  // Save collaborative simulation data with integration
  const saveDataPointDetails = useCallback(async () => {
    if (isDemoMode || isTeamSimulation) return // Skip for demo mode and team simulations

    // Create base simulation data
    const baseData = {
      ...finalData,
      length: duration,
      time: moment("2024-01-01").seconds(secondsElapsed).format("HH:mm:ss"),
      log: [...messages]
    }
    
    // Merge with collaborative data
    const enhancedData = mergeCollaborativeData(baseData, collaborationState, secondsElapsed)
    
    const time = moment("2024-01-01").seconds(secondsElapsed).format("HH:mm:ss")
    const payload = {
      runId: Number(runId),
      datetime: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
      runTimer: time,
      data: enhancedData,
    }
    
    try {
      await postSavepointByRunId(payload)
      console.log("Collaborative savepoint saved successfully", enhancedData)
    } catch (error) {
      console.error("Collaborative savepoint error", error)
    }
  }, [finalData, duration, secondsElapsed, messages, collaborationState, runId, postSavepointByRunId, isDemoMode])

  const saveFinalDataPoint = useCallback(async () => {
    if (isDemoMode) return // Skip for demo mode only
    if (hasSavedFinalData) {
      console.debug("Final data already saved, skipping duplicate save")
      return
    }

    // BUG FIX: Handle team simulation ending properly
    // Team simulations don't have a runId, so we handle them separately
    if (isTeamSimulation) {
      console.log("🏁 Team simulation time limit reached - ending simulation")
      setHasSavedFinalData(true)

      // Stop the timer
      if (timer) {
        dispatch(destroyTimer())
      }

      // Navigate to team simulation completion page (no auth required)
      navigate(`/team-simulations/${teamSimulationId}/complete`)
      return
    }

    // Validate that we have meaningful simulation data before saving
    // This prevents saving/downloading when simulation hasn't properly loaded
    if (!duration || convertToSeconds(duration) === 0) {
      console.warn("saveFinalDataPoint called but duration is not set, skipping")
      return
    }

    // Mark as saved immediately to prevent race conditions
    setHasSavedFinalData(true)

    // Create comprehensive final results with collaborative data
    const baseData = {
      ...finalData,
      endDate: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
      log: [...messages],
      time: "", // Final time is empty as per original logic
    }

    const collaborativeFinalResults = createCollaborativeFinalResults(baseData, collaborationState)

    setFinalData(collaborativeFinalResults)

    const payload = {
      runId: Number(runId),
      body: collaborativeFinalResults,
    }

    try {
      await postFinalResults(payload)
      console.log("Collaborative final results saved successfully")

      // Navigate to completion page
      navigate("/simulations/complete")
    } catch (error) {
      console.error("Collaborative final results error", error)
      // Reset flag on error so user can retry
      setHasSavedFinalData(false)
    }
  }, [finalData, messages, collaborationState, runId, postFinalResults, navigate, isDemoMode, isTeamSimulation, hasSavedFinalData, duration, timer, dispatch])

  // Consolidated initialization
  useEffect(() => {
    let isMounted = true
    
    const initializeApp = async () => {
      try {
        console.log('Starting app initialization...', { 
          isDemoMode, 
          isTeamSimulation, 
          testId: stableTestId, 
          runId: stableRunId 
        })
        setInitializationStep('initializing')
        
        if (isDemoMode) {
          setInitializationStep('loading_simulation')
          
          // Simple demo setup
          const demoMessages: MessagesWithExtraType[] = [
            {
              id: 1,
              cell: "F2",
              eventId: "1412",
              title: "Competitor Attack",
              description: "Your largest competitor, Sleeves of Leaves, has just launched a digital advertising campaign directly calling out Roots & Shoots for small portion sizes.",
              date: new Date().toISOString(),
              elapsedTime: "00:00:00",
              response: "",
              action: 1,
              messageType: "Incoming Message",
              validateResponse: "This report has been confirmed by your office.",
              type: "message",
              currentDate: new Date().toISOString(),
              categoryId: null,
              showResponse: false
            },
            {
              id: 2,
              cell: "G4",
              eventId: "1413",
              title: "Intern Management Issue",
              description: "One of your direct reports, Lisa, reaches out to ask about a new intern, Ryan, on her team. The concern is that Ryan is \"lost, confused and distracted.\"",
              date: new Date().toISOString(),
              elapsedTime: "00:01:30",
              response: "",
              action: 2,
              messageType: "Incoming Message",
              validateResponse: "This is based on internal company conversations.",
              type: "message",
              currentDate: new Date().toISOString(),
              categoryId: null,
              showResponse: false
            }
          ];
          
          // Add messages directly
          demoMessages.forEach(message => {
            dispatch(addMessage(message));
          });
          
          // Set up demo simulation data for decision tree
          const demoInformation = [{
            id: 46041,
            text: "Information Search",
            icon: null,
            order: 1,
            actionSentence: null,
            planSentence: null,
            isInfoSearch: 1,
            infoSearchCategory: null,
            measureReference: null,
            categories: [
              {
                id: 46042,
                text: "CEO",
                icon: null,
                order: 1,
                actionSentence: null,
                planSentence: null,
                isInfoSearch: 0,
                infoSearchCategory: null,
                measureReference: null,
                categories: [
                  {
                    id: 46043,
                    text: "Current relationships with",
                    icon: null,
                    order: 1,
                    actionSentence: null,
                    planSentence: null,
                    isInfoSearch: 0,
                    infoSearchCategory: null,
                    measureReference: null,
                    categories: [
                      {
                        id: 46044,
                        text: "Board of directors",
                        icon: null,
                        order: 1,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7300,
                          response: "The CEO and board of directors have a contentious relationship.",
                          time: "00:00:00"
                        }]
                      },
                      {
                        id: 46045,
                        text: "Shareholders",
                        icon: null,
                        order: 2,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7301,
                          response: "The CEO is known for focusing on shareholder interests.",
                          time: "00:00:00"
                        }]
                      },
                      {
                        id: 46047,
                        text: "Employees",
                        icon: null,
                        order: 4,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7303,
                          response: "The CEO is deeply invested in employee growth and corporate wellness.",
                          time: "00:00:00"
                        }]
                      }
                    ]
                  },
                  {
                    id: 46051,
                    text: "Background information",
                    icon: null,
                    order: 2,
                    actionSentence: null,
                    planSentence: null,
                    isInfoSearch: 0,
                    infoSearchCategory: null,
                    measureReference: null,
                    categories: [
                      {
                        id: 46052,
                        text: "History with Roots & Shoots",
                        icon: null,
                        order: 8,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7307,
                          response: "Roots & Shoots hired the CEO a year ago to help boost sales and capture market share from Sleeves of Leaves.",
                          time: "00:00:00"
                        }]
                      }
                    ]
                  }
                ]
              },
              {
                id: 46079,
                text: "Direct reports",
                icon: null,
                order: 4,
                actionSentence: null,
                planSentence: null,
                isInfoSearch: 0,
                infoSearchCategory: null,
                measureReference: null,
                categories: [
                  {
                    id: 46085,
                    text: "Morale",
                    icon: null,
                    order: 2,
                    actionSentence: null,
                    planSentence: null,
                    isInfoSearch: 0,
                    infoSearchCategory: null,
                    measureReference: null,
                    categories: [
                      {
                        id: 46087,
                        text: "Lisa",
                        icon: null,
                        order: 34,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7333,
                          response: "Lisa is having a hard time navigating this situation.",
                          time: "00:00:00"
                        }]
                      }
                    ]
                  }
                ]
              },
              {
                id: 46092,
                text: "Summer interns",
                icon: null,
                order: 5,
                actionSentence: null,
                planSentence: null,
                isInfoSearch: 0,
                infoSearchCategory: null,
                measureReference: null,
                categories: [
                  {
                    id: 46098,
                    text: "Morale",
                    icon: null,
                    order: 2,
                    actionSentence: null,
                    planSentence: null,
                    isInfoSearch: 0,
                    infoSearchCategory: null,
                    measureReference: null,
                    categories: [
                      {
                        id: 46100,
                        text: "Ryan",
                        icon: null,
                        order: 40,
                        actionSentence: "",
                        planSentence: "",
                        isInfoSearch: 0,
                        infoSearchCategory: null,
                        measureReference: "",
                        categories: [],
                        responses: [{
                          id: 7343,
                          response: "Ryan is angry and frustrated with the situation.",
                          time: "00:00:00"
                        }]
                      }
                    ]
                  }
                ]
              }
            ]
          }];

          const demoChoices = [
            {
              id: 46231,
              text: "CEO",
              icon: null,
              order: 1,
              actionSentence: null,
              planSentence: null,
              isInfoSearch: 0,
              infoSearchCategory: "46042",
              measureReference: null,
              categories: [
                {
                  id: 46232,
                  text: "Communicate",
                  icon: null,
                  order: 1,
                  actionSentence: null,
                  planSentence: null,
                  isInfoSearch: 0,
                  infoSearchCategory: null,
                  measureReference: null,
                  categories: [
                    {
                      id: 46233,
                      text: "Short term strategy to manage current events",
                      icon: null,
                      order: 113,
                      actionSentence: "You have made the decision to communicate short term strategies to manage current events to the CEO.",
                      planSentence: "You have made a plan to communicate short term strategies to manage current events to the CEO.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "3,5",
                      categories: []
                    },
                    {
                      id: 46241,
                      text: "Bring an issue to their attention",
                      icon: null,
                      order: 121,
                      actionSentence: "You have made the decision to bring important issues to the CEO's attention.",
                      planSentence: "You have made a plan to bring important issues to the CEO's attention.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "5",
                      categories: []
                    }
                  ]
                }
              ]
            },
            {
              id: 46295,
              text: "Direct Reports",
              icon: null,
              order: 4,
              actionSentence: null,
              planSentence: null,
              isInfoSearch: 0,
              infoSearchCategory: "46079",
              measureReference: null,
              categories: [
                {
                  id: 46312,
                  text: "Communicate",
                  icon: null,
                  order: 3,
                  actionSentence: null,
                  planSentence: null,
                  isInfoSearch: 0,
                  infoSearchCategory: null,
                  measureReference: null,
                  categories: [
                    {
                      id: 46314,
                      text: "Providing positive feedback",
                      icon: null,
                      order: 179,
                      actionSentence: "You have made the decision to communicate positive feedback to direct reports.",
                      planSentence: "You have made a plan to communicate positive feedback to direct reports.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "4,5",
                      categories: []
                    },
                    {
                      id: 46317,
                      text: "Praise",
                      icon: null,
                      order: 182,
                      actionSentence: "You have made the decision to praise your direct reports.",
                      planSentence: "You have made a plan to praise your direct reports.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "4",
                      categories: []
                    }
                  ]
                },
                {
                  id: 46296,
                  text: "Program structure",
                  icon: null,
                  order: 1,
                  actionSentence: null,
                  planSentence: null,
                  isInfoSearch: 0,
                  infoSearchCategory: null,
                  measureReference: null,
                  categories: [
                    {
                      id: 46299,
                      text: "Offer support",
                      icon: null,
                      order: 166,
                      actionSentence: "You have made the decision to offer support to your direct reports.",
                      planSentence: "You have made a plan to offer support to your direct reports.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "4",
                      categories: []
                    }
                  ]
                }
              ]
            },
            {
              id: 46318,
              text: "Summer Interns",
              icon: null,
              order: 5,
              actionSentence: null,
              planSentence: null,
              isInfoSearch: 0,
              infoSearchCategory: "46092",
              measureReference: null,
              categories: [
                {
                  id: 46335,
                  text: "Communicate",
                  icon: null,
                  order: 3,
                  actionSentence: null,
                  planSentence: null,
                  isInfoSearch: 0,
                  infoSearchCategory: null,
                  measureReference: null,
                  categories: [
                    {
                      id: 46341,
                      text: "Offer support",
                      icon: null,
                      order: 202,
                      actionSentence: "You have made the decision to offer support to the summer interns.",
                      planSentence: "You have made a plan to offer support to the summer interns.",
                      isInfoSearch: 0,
                      infoSearchCategory: null,
                      measureReference: "4,5",
                      categories: []
                    }
                  ]
                }
              ]
            }
          ];

          // Set up the simulation data using the proper action
          dispatch(simulationSlice.actions.setInformation(demoInformation));
          dispatch(simulationSlice.actions.setChoices(demoChoices));
          
          setInitializationStep('setting_up_collaboration')
          
          // Set up demo collaboration
          try {
            const demoRoom: SimulationRoom = {
              id: 'demo-roots-shoots',
              name: 'Roots & Shoots Crisis Management - Demo',
              description: 'Environmental conservation crisis simulation',
              testId: 1,
              organizationId: 1,
              maxParticipants: 6,
              currentParticipants: 3,
              status: RoomStatus.ACTIVE,
              createdBy: 1,
              createdAt: new Date().toISOString(),
              votingThreshold: VotingThreshold.SIMPLE_MAJORITY,
              votingTimeLimit: 300,
            }
            
            dispatch(setCurrentRoom(demoRoom))
            dispatch(setCurrentUserId(1))
            
            const demoParticipants = [
              {
                id: '1',
                userId: 1,
                firstName: 'Demo',
                lastName: 'User',
                email: 'demo@example.com',
                role: ParticipantRole.LEADER,
                status: ParticipantStatus.CONNECTED,
                joinedAt: new Date().toISOString(),
                lastActive: new Date().toISOString(),
              },
              {
                id: '2',
                userId: 2,
                firstName: 'Alice',
                lastName: 'Johnson',
                email: 'alice@example.com',
                role: ParticipantRole.MEMBER,
                status: ParticipantStatus.CONNECTED,
                joinedAt: new Date().toISOString(),
                lastActive: new Date().toISOString(),
              },
              {
                id: '3',
                userId: 3,
                firstName: 'Bob',
                lastName: 'Wilson',
                email: 'bob@example.com',
                role: ParticipantRole.MEMBER,
                status: ParticipantStatus.CONNECTED,
                joinedAt: new Date().toISOString(),
                lastActive: new Date().toISOString(),
              }
            ]
            
            dispatch(setParticipants(demoParticipants))
          } catch (collaborationError) {
            console.warn('Failed to set up collaboration, continuing without:', collaborationError)
          }
          
          // Start timer
          dispatch(createTimer())
        } else if (isTeamSimulation) {
          // Team simulation setup - load REAL scenario data, not demo data
          console.log('Setting up team simulation with real data...')
          setInitializationStep('loading_simulation')

          // Timer initialization will be handled after joining the room,
          // using the room's startedAt timestamp to calculate elapsed time.
          // This prevents timer reset on page refresh.

          // Team simulations should load real scenario data like individual simulations
          // The API call for simResponse should provide the real Roots & Shoots data
          // We'll set up the simulation state in the useEffect below when simResponse loads

          setInitializationStep('setting_up_collaboration')

          try {
            // Set up basic collaboration state without complex features
            setInitializationStep('complete')
            console.log('Team simulation initialization completed')

            if (isMounted) {
              setIsAppReady(true)
              console.log('Team simulation ready!')
            }

            // Start timer - this will be used when real scenario data loads
            dispatch(createTimer())
          } catch (error) {
            console.error('Error setting up team simulation:', error)
            if (isMounted) {
              setIsAppReady(true)
            }
          }
        } else {
          // Regular collaborative simulation - might need auth
          console.log('Regular collaborative simulation detected, but no auth handling implemented yet')
          setInitializationStep('complete')
          
          if (isMounted) {
            setIsAppReady(true)
            console.log('Regular simulation ready!')
          }
        }
        
        setInitializationStep('complete')
        console.log('App initialization completed, setting ready state')
        
        if (isMounted) {
          setIsAppReady(true)
          console.log('App is now ready!')
        }
        
      } catch (error) {
        console.error('Error during app initialization:', error)
        if (isMounted) {
          setIsAppReady(true)
        }
      }
    }
    
    initializeApp()
    
    return () => {
      isMounted = false
    }
  }, [isDemoMode, isTeamSimulation, dispatch, stableRunId, stableTestId])

  // Simplified effects for non-demo mode (including team simulations)
  useEffect(() => {
    if (isDemoMode || !isAppReady) return
    
    if (simLoadIsSuccess && simResponse?.data && simResponse.data.length > 0) {
      dispatch(setupSimulation(simResponse))
      
      // Initialize final data with simulation info
      setFinalData((prevFinalData) => ({
        ...prevFinalData,
        id: simResponse.data && simResponse?.data[0].id !== undefined ? simResponse?.data[0]?.id : 0,
        name: simResponse.data && simResponse?.data[0].title !== undefined ? simResponse?.data[0]?.title : "",
        startDate: moment(new Date()).utc().format("YYYY-MM-DD hh:mm:ss"),
        time: moment("2024-01-01").seconds(secondsElapsed ?? 0).format("HH:mm:ss"),
      }))
    }
  }, [simLoadIsSuccess, simResponse, isDemoMode, dispatch, isAppReady, secondsElapsed])

  useEffect(() => {
    if (isDemoMode || !isAppReady) return
    
    const interval = setInterval(() => {
      if (secondsElapsed > 0 && timer) {
        callSavePoint()
      }
    }, SAVEPOINT_TIMER)

    return () => clearInterval(interval)
  }, [secondsElapsed, timer, isDemoMode, callSavePoint, isAppReady])

  // Monitor simulation time and save collaborative data
  useEffect(() => {
    if (isDemoMode || secondsElapsed === 0 || !simLoadIsSuccess || !timer) return

    const durationSeconds = convertToSeconds(duration)
    // Skip if duration is not set (prevents triggering on join before data loads)
    if (durationSeconds === 0) return

    // Save collaborative data periodically
    if (secondsElapsed > 0 && secondsElapsed % SAVEPOINT_TIMER === 0) {
      saveDataPointDetails()
    }

    // End simulation when time is up (only if not already saved)
    if (!hasSavedFinalData && secondsElapsed >= durationSeconds) {
      saveFinalDataPoint()
      console.debug("Collaborative simulation finished")
    }
  }, [secondsElapsed, duration, isDemoMode, simLoadIsSuccess, timer, saveDataPointDetails, saveFinalDataPoint, hasSavedFinalData])

  // Save when messages are loaded
  useEffect(() => {
    if (isDemoMode || !isMessagesLoaded || secondsElapsed === 0) return
    
    saveDataPointDetails()
  }, [messages, isDemoMode, isMessagesLoaded, secondsElapsed, saveDataPointDetails])

  // Add this effect to handle collaboration readiness
  const [isCollaborationReady, setIsCollaborationReady] = useState(false)
  
  useEffect(() => {
    if (currentRoom && participants.length > 0) {
      const timer = setTimeout(() => {
        setIsCollaborationReady(true)
      }, 100)
      return () => clearTimeout(timer)
    } else {
      setIsCollaborationReady(false)
    }
  }, [currentRoom, participants.length])

  // Early returns for loading states
  // Skip check for team simulations - they use teamSimulationId instead of testIdParam/runIdParam
  if (!isDemoMode && !isTeamSimulation && (!testIdParam || !runIdParam)) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading simulation parameters...</div>
      </div>
    )
  }

  if (simLoadIsFetching && !isDemoMode) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-600">Loading simulation...</div>
      </div>
    )
  }

  if (!isAppReady) {
    console.log('Still waiting for app to be ready:', { isAppReady, initializationStep, isDemoMode, isTeamSimulation })
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">
            {initializationStep === 'starting' && 'Starting application...'}
            {initializationStep === 'initializing' && 'Initializing...'}
            {initializationStep === 'loading_simulation' && 'Loading simulation...'}
            {initializationStep === 'setting_up_collaboration' && 'Setting up collaboration...'}
            {initializationStep === 'complete' && 'Finalizing...'}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            Debug: {isDemoMode ? 'demo' : isTeamSimulation ? 'team' : 'regular'} | Step: {initializationStep}
          </div>
        </div>
      </div>
    )
  }

  // Main render
  return (
    <div className="relative bg-base-white w-full h-[100dvh] overflow-hidden flex flex-row">
      {/* Hide sidebar for team simulations to maximize screen space */}
      {!isTeamSimulation && <SidebarMinimal />}
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <NotificationContainer errorMessage={""} successMessage={""} />
        {/* Hide header for team simulations - cleaner interface */}
        {!isTeamSimulation && <HeaderSection />}
        <div
          id="simulationContainer"
          className={`relative overflow-hidden box-border w-full flex-1 flex flex-col justify-start py-6 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium ${isTeamSimulation ? 'px-6' : 'pl-9 pr-6'}`}>
          
          <div>
            <SimulationHeader
              setModalBreaktimeOpen={setModalBreaktimeOpen}
              simulation={isDemoMode ? [] : (simResponse?.data || [])}
            />
          </div>

          {/* New Layout: Video on Top, Three Sections Below */}
          <div className="w-full flex-1 flex flex-col gap-6 text-base text-gray-700 min-h-0 overflow-hidden">
            <DndContext
              autoScroll={false}
              collisionDetection={rectIntersection}
              modifiers={[restrictToWindowEdges]}>

              {/* Lobby/Waiting Room - shown when waiting for scheduled start time or host to start */}
              {/* BUG FIX: Use confirmedStatus instead of raw API status to prevent flickering during transitions */}
              {/* BUG FIX: Don't show waiting room when isAutoStarting - show "starting" state instead */}
              {isTeamSimulation && currentRoom && isCollaborationReady && !simulationStarted && !isAutoStarting && (confirmedStatus === 'pending' || (!confirmedStatus && teamSimulationData?.data?.status === 'pending')) ? (
                <div className="w-full flex-1 bg-gradient-to-br from-blue-50 to-indigo-100 border border-gray-200 rounded-lg flex flex-col items-center justify-center">
                  <div className="text-center p-8 max-w-lg">
                    <div className="w-20 h-20 bg-blue-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Waiting Room</h2>
                    <p className="text-gray-600 mb-6">
                      {timeUntilStart !== null
                        ? 'The simulation is scheduled to begin soon. Please wait here until the start time.'
                        : 'Waiting for the host to start the simulation. Please wait here.'}
                    </p>

                    {/* Countdown Timer - only shown if there's a scheduled time */}
                    {timeUntilStart !== null ? (
                      <div className="bg-white rounded-xl p-6 shadow-md mb-6">
                        <p className="text-sm text-gray-500 mb-2">Starting in</p>
                        <div className="text-4xl font-bold text-blue-600">
                          {Math.floor(timeUntilStart / 3600) > 0 && (
                            <span>{Math.floor(timeUntilStart / 3600)}h </span>
                          )}
                          {Math.floor((timeUntilStart % 3600) / 60) > 0 && (
                            <span>{Math.floor((timeUntilStart % 3600) / 60)}m </span>
                          )}
                          <span>{timeUntilStart % 60}s</span>
                        </div>
                        {teamSimulationData?.data?.scheduledStartTime && (
                          <p className="text-xs text-gray-400 mt-2">
                            Scheduled: {new Date(teamSimulationData.data.scheduledStartTime).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl p-6 shadow-md mb-6">
                        <p className="text-sm text-gray-500 mb-2">Status</p>
                        <div className="text-xl font-semibold text-blue-600">
                          Waiting for host...
                        </div>
                      </div>
                    )}

                    {/* Connected Participants */}
                    <div className="bg-white rounded-xl p-4 shadow-md">
                      <p className="text-sm text-gray-500 mb-3">Connected Participants ({participants.length})</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {participants.map((p) => (
                          <div key={p.id} className="flex items-center gap-2 bg-gray-100 rounded-full px-3 py-1">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-gray-700">
                              {p.firstName} {p.lastName}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Video Preview - Let users test A/V and chat while waiting */}
                    {collaborationSession && (
                      <div className="mt-6">
                        <p className="text-sm text-gray-500 mb-3">Test your audio/video and chat while waiting</p>
                        <div className="bg-gray-900 rounded-lg overflow-hidden" style={{ height: '320px' }}>
                          <LiveKitVideoGrid
                            collaborationSession={collaborationSession}
                            participants={participants}
                            showChat={true}
                            showControls={true}
                            compactLayout={true}
                            className="w-full h-full"
                            roomId={currentRoom?.id}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : currentRoom && isCollaborationReady && simulationStarted ? (
                <>
                  {/* Top Section - Video Display with LiveKit Chat (side-by-side) */}
                  <div className={`w-full bg-white rounded-lg border border-gray-300 shadow-md flex flex-col relative flex-shrink-0`} style={{ height: isTeamSimulation ? '280px' : '400px' }}>
                      {/* LiveKit Video Grid Component */}
                      {collaborationSession && (
                        <div className="w-full h-full">
                          <LiveKitVideoGrid
                            collaborationSession={collaborationSession}
                            participants={participants}
                            showChat={true}
                            showControls={true}
                            compactLayout={isTeamSimulation}
                            className="w-full h-full"
                            roomId={currentRoom?.id}
                          />
                        </div>
                      )}
                  </div>

                  {/* Bottom Section - Three Columns: Messages, Actions, Votes */}
                  <div className="w-full flex-1 flex flex-row gap-4 min-h-0">
                    {/* Left Column - Messages */}
                    <div className="w-1/3 flex flex-col bg-white rounded-lg shadow-md border border-gray-300 min-h-0">
                      <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg text-center flex-shrink-0">
                        <h2 className="text-base font-semibold text-gray-900">Messages</h2>
                      </div>
                      <div className="flex-1 min-h-0 overflow-auto">
                        <MessagesWithSearch />
                      </div>
                    </div>

                    {/* Middle Column - Actions */}
                    {/* BUG FIX: Changed overflow-auto to overflow-hidden so internal scroll containers work */}
                    {/* This keeps the Submit/Connections buttons visible at the bottom */}
                    <div className="w-1/3 flex flex-col bg-white rounded-lg shadow-md border border-gray-300 min-h-0 overflow-hidden">
                      <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg text-center flex-shrink-0">
                        <h2 className="text-base font-semibold text-gray-900">Actions</h2>
                      </div>
                      {/* BUG FIX: Added flex flex-col to propagate flex layout to children */}
                      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                        {/* BUG FIX: Wrap in error boundary to prevent crashes */}
                        <SimulationErrorBoundary>
                          <TeamActionsCenter />
                        </SimulationErrorBoundary>
                      </div>
                    </div>

                    {/* Right Column - Votes */}
                    <div className="w-1/3 flex flex-col bg-white rounded-lg shadow-md border border-gray-300 min-h-0">
                      <div className="p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex-shrink-0">
                        <div className="flex justify-center items-center">
                          <h2 className="text-base font-semibold text-gray-900">Votes</h2>
                          {collaborationState.pendingChoices.length > 0 && (
                            <div className="bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full font-medium ml-2">
                              {collaborationState.pendingChoices.length} pending
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex-1 p-3 overflow-auto min-h-0">
                        {/* BUG FIX: Wrap in error boundary to prevent crashes during execution */}
                        <SimulationErrorBoundary>
                          <VotingPanel
                            choices={collaborationState.pendingChoices}
                            currentUserId={currentUserId || 0}
                            onVote={(choiceId, decision) => {
                              console.log('Vote cast:', choiceId, decision)
                            }}
                          />
                        </SimulationErrorBoundary>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Fallback for when not in a room */
                <div className="w-full flex-1 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-center p-8">
                    {/* Team simulation auto-join: show loading state */}
                    {isTeamSimulation ? (
                      isAutoStarting ? (
                        /* BUG FIX: Show "starting simulation" when auto-start is in progress */
                        /* This prevents the waiting room from flickering briefly */
                        <>
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                          <h3 className="text-lg font-medium text-gray-700 mb-2">
                            Starting Simulation...
                          </h3>
                          <p className="text-sm text-gray-500">
                            The scheduled time has arrived. The simulation is starting now.
                          </p>
                        </>
                      ) : isAutoJoining || isLoadingTeamSimulation ? (
                        <>
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <h3 className="text-lg font-medium text-gray-700 mb-2">
                            {isLoadingTeamSimulation ? 'Loading simulation...' : 'Joining team simulation...'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Please wait while we set up your session
                          </p>
                        </>
                      ) : autoJoinError ? (
                        <>
                          <div className="w-16 h-16 bg-red-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                          </div>
                          <h3 className="text-lg font-medium text-red-700 mb-2">Unable to Join</h3>
                          <p className="text-sm text-red-600 mb-4">{autoJoinError}</p>
                          <div className="flex gap-2 justify-center">
                            <button
                              onClick={() => {
                                setAutoJoinRetryCount(0)
                                setAutoJoinError(null)
                              }}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                              Retry
                            </button>
                            <button
                              onClick={() => window.location.reload()}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                            >
                              Refresh Page
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                          <h3 className="text-lg font-medium text-gray-700 mb-2">Preparing simulation...</h3>
                          <p className="text-sm text-gray-500">
                            Setting up your team session
                          </p>
                        </>
                      )
                    ) : (
                      /* Regular collaborative simulation: show Create Room button */
                      <>
                        <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-4 flex items-center justify-center">
                          <UserGroupIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-700 mb-2">Join Collaboration</h3>
                        <p className="text-sm text-gray-500 mb-4">
                          Create or join a room to start collaborating with your team
                        </p>
                        <button
                          onClick={() => setShowCreateRoomModal(true)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Create Room
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </DndContext>
          </div>
        </div>
      </div>

      {/* Modals with error boundaries */}
      {isModalBreaktimeOpen && (
        <ModalBreaktime closeModalBreaktime={closeModalBreaktime} />
      )}
      
      {/* Collaboration Modals with lazy loading */}
      <Suspense fallback={null}>
        {votingModalOpen && <VotingModal />}

        {/* Only show CreateRoomModal for non-team simulations */}
        {!isTeamSimulation && (
          <CreateRoomModal
            isOpen={showCreateRoomModal}
            onClose={() => setShowCreateRoomModal(false)}
            onCreateRoom={handleCreateRoom}
            testId={testId}
            organizationId={isDemoMode ? 1 : (organization?.id ? parseInt(organization.id) : 1)}
          />
        )}
      </Suspense>
    </div>
  )
}

export default CollaborativeSimulation 
